import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { isMobileViewport } from "../components/UserInfo";

/** The search param that records which pane the mobile drill-down is showing. */
export const VIEW_PARAM = "view";

/**
 * FR-006 permits the location to encode *what is on screen*; it forbids encoding *which user*.
 *
 * `"list"` is the default and is never written to the URL — an absent param means the list, so a
 * bare `/cc/configuration/usermanager` is the list view and stays the canonical entry point.
 */
export const DRILL_DOWN_VIEWS = ["list", "detail"] as const;

export type DrillDownView = (typeof DRILL_DOWN_VIEWS)[number];

export interface DrillDownViewState {
  /** Which pane the mobile layout shows. Always `"list"` on a fresh load. */
  view: DrillDownView;
  /** Show the detail pane, pushing a history entry so hardware Back returns to the list. */
  showDetail: () => void;
  /** Return to the list pane. */
  showList: () => void;
}

/**
 * Owns the mobile drill-down's view state — in the **location**, so the hardware Back button works.
 *
 * ## Why this is a search param and NOT `usermanager/:userId`
 *
 * TA4236901 asked for real routes (`/users` and `/users/:id`) so that Back works and a detail view
 * is linkable, with the note that "users on mobile will press the system back button before they
 * find yours". The first half of that is right and is what this hook delivers. **The second half is
 * forbidden by a decided requirement**, so it is not built:
 *
 * > **FR-006**: The page's location identifies the **account and session context** … and **MUST
 * > NOT** encode which user is currently selected. Selection is page state: it is not bookmarkable,
 * > not shareable by link, and resets on reload.
 *
 * D-07 settled that on 2026-09-04 and recorded why: a shareable link to a selected user "was added
 * on the author's judgment with no source asking for it, and the tool being replaced keeps selection
 * in page state too". So this encodes **the view, not the user**. A cold load of `?view=detail`
 * lands on the detail pane showing whichever user FR-006 auto-selects — the first in the list —
 * which is the behaviour that requirement already mandates. Nothing in the location says *who*.
 *
 * There is direct precedent: `useUserInfoTabs` already keeps the open tab in `?tab=`.
 *
 * ## The one deliberate difference from `useUserInfoTabs`: no `replace`
 *
 * Tab switches use `setSearchParams(next, { replace: true })` so the strip does not fill the history
 * stack. **`showDetail` must NOT do that** — a replaced entry is exactly what would make Back skip
 * over the list and leave the page entirely, which is the whole defect this hook exists to fix.
 * `showList` *does* replace, because a tap on Back should not be undoable into the detail view it
 * just left.
 *
 * **Confirmed working against the real browser's Back button** in the running app (reviewer,
 * 2026-09-23 — see the TA4236901 verification record in the spec bundle's `tasks.md`).
 * `useDrillDownView.spec.ts` is the standing regression guard, because no component test can drive
 * `popstate` against the `createMemoryRouter` that `mountExtendedDDC` mounts.
 *
 * **Known, accepted wart.** Using the *in-app* back control leaves one redundant history entry:
 * `showDetail` pushes (`[list, detail]`) and `showList` replaces the current one (`[list, list]`),
 * so the first browser-Back press afterwards moves from the list to the list and appears to do
 * nothing. The obvious alternative — having `showList` call `navigate(-1)` — is worse: on a cold
 * load of `?view=detail` there is no previous entry within the page, so it would take the operator
 * out of Configuration entirely. A dead Back press is a much smaller cost than an unexpected exit.
 *
 * ## How this interacts with the pending-edits guard
 *
 * `usePendingEditsGuard` blocks navigation only when the **pathname** changes, which it must — a
 * bare `useBlocker(isDirty)` already broke FR-022 once by prompting on every tab switch. A search
 * param leaves the pathname alone, so:
 *
 * | Action | pathname | Prompted? | Correct because |
 * |---|---|---|---|
 * | Back, detail → list | unchanged | no | the shell stays mounted, so the edits survive. Nothing is lost, so nothing should ask. |
 * | tap a *different* user | unchanged | yes, via `requestAction` | FR-023 — the subject changes, so the edits would be lost |
 * | leave Configuration | changed | yes, via the blocker | FR-024 |
 *
 * That falls out of the existing design rather than needing anything new here. **Do not** "fix" the
 * blocker to compare full locations instead of pathnames to make Back prompt: going back to the
 * list loses nothing.
 *
 * ## Desktop pushes no history
 *
 * At desktop both panes are visible, so a selection changes nothing about what is on screen and an
 * entry would make Back appear to do nothing. `isMobileViewport()` is read **inside the click
 * handler**, where the viewport is settled and no render depends on it — see its own comment for why
 * that distinction matters.
 */
export const useDrillDownView = (): DrillDownViewState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const view: DrillDownView =
    searchParams.get(VIEW_PARAM) === "detail" ? "detail" : "list";

  /*
   * A fresh `URLSearchParams` from the current one on every call, so `?tab=` and the rest survive.
   * Mutating `searchParams` in place is a react-router footgun: it is the live object the router
   * hands out, and edits to it do not re-render.
   */
  const showDetail = useCallback(() => {
    if (!isMobileViewport()) {
      return;
    }

    setSearchParams(
      current => {
        const next = new URLSearchParams(current);
        next.set(VIEW_PARAM, "detail");

        return next;
      },
      /* NOT `replace` — this entry is what Back comes back to. See the hook's comment. */
      { replace: false }
    );
  }, [setSearchParams]);

  const showList = useCallback(() => {
    setSearchParams(
      current => {
        const next = new URLSearchParams(current);
        /*
         * Deleted rather than set to `"list"`. The list is the default, so its URL is the bare one —
         * which keeps `?view=list` from becoming a second spelling of the same state that tests and
         * links would then have to know about.
         */
        next.delete(VIEW_PARAM);

        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return { showDetail, showList, view };
};
